import React, { FC, ReactElement } from 'react'
import warning from 'warning'
import styled from 'styled-components'

import { dasherize } from 'utils'
import FormLabel from '../FormLabel'
import FormError from '../FormError'
import { withBox } from 'components/Box'
import { HelpText, HelpTextContainer, ErrorList } from './FormField.styles'

// Types
interface FormFieldProps {
  id?: string
  name?: string
  label?: string | ReactElement
  errorText?: string | string[]
  children?: ReactElement
  help?: string
}

// Helper functions
const renderErrorTextArray = (errorTextArray: string[]) => (
  <ErrorList>
    {errorTextArray.map((errorText, i) => (
      <li key={i}>
        <FormError>{errorText}</FormError>
      </li>
    ))}
  </ErrorList>
)

const renderErrorText = (errorText: string) =>
  errorText ? renderErrorTextArray([errorText]) : null

// Unstyled FormField
const FormField: FC<FormFieldProps> = ({
  id,
  name,
  label,
  errorText,
  help,
  children,
  ...props
}) => {
  warning(
    id || name,
    'Not enough information to generate a unique id. Please provide an id or a name.'
  )

  const isInvalid = errorText && errorText.length > 0
  const inputId = dasherize(id || name) + '-input' // use id or name to generate unique id for input element
  const renderChild = (child: any) =>
    React.cloneElement(child, {
      id: child.id || inputId,
      invalid: isInvalid,
    })

  return (
    <div {...props}>
      {label && <FormLabel htmlFor={inputId}>{label}</FormLabel>}
      {children && React.Children.map(children, renderChild)}
      {errorText &&
        (Array.isArray(errorText)
          ? renderErrorTextArray(errorText)
          : renderErrorText(errorText))}
      {help && (
        <HelpTextContainer>
          <HelpText>{help}</HelpText>
        </HelpTextContainer>
      )}
    </div>
  )
}

const StyledForm = styled(withBox(FormField))`
  position: relative;

  &:last-child {
    margin-bottom: 0;
  }
`

StyledForm.defaultProps = {
  mt: 0,
  mb: 6,
}

export default StyledForm
